<script setup lang="ts">
import useEmblaCarousel from 'embla-carousel-vue'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import type { IntRange } from 'type-fest'

const { t, tm, rt } = useI18n()

const [emblaRef, emblaApi] = useEmblaCarousel({
  align: 'start',
  slidesToScroll: 'auto',
  loop: false,
}, [
  WheelGesturesPlugin(),
])

const { selectedIndex, scrollSnaps, handleClick } = useEmblaDots(emblaApi)
const { isPrevDisabled, isNextDisabled, handlePrevious, handleNext } = useEmblaButtons(emblaApi)

function getTestimonyImage(index: StringifiedInt<IntRange<0, 5>>) {
  return match(index)
    .with('0', () => `${S3_ASSETS_URL}/home/text-testimonials/trattoria-a-vucchella.jpg`)
    .with('1', () => `${S3_ASSETS_URL}/home/text-testimonials/bullpen-bbq-taphouse.jpg`)
    .with('2', () => `${S3_ASSETS_URL}/home/text-testimonials/ristorante-luce.jpg`)
    .with('3', () => `${S3_ASSETS_URL}/home/text-testimonials/chubbys.jpg`)
    .with('4', () => `${S3_ASSETS_URL}/home/text-testimonials/memories-bar-grill.jpg`)
    .exhaustive()
}
</script>

<template>
  <div
    id="review"
    class="relative overflow-hidden"
  >
    <section class="mx-auto max-w-screen-xl px-4 py-8 lg:px-8 lg:py-16 2xl:overflow-hidden">
      <UiSectionHeadline
        :title="t('home.section.text_testimonials.title')"
        :subtitle="t('home.section.text_testimonials.subtitle')"
      />

      <main class="mt-12 flex flex-col gap-y-8">
        <div
          ref="emblaRef"
          class="relative select-none"
        >
          <div class="flex gap-x-5 [backface-visibility:hidden]">
            <div
              v-for="(item, itemIndex) in tm('home.section.text_testimonials.items')"
              :key="itemIndex"
              class="relative flex shrink-0 grow-0 basis-[calc(100%-1.25rem*2)] justify-stretch lg:basis-[calc((100%-1.25rem*2)/2)]"
            >
              <div class="w-full cursor-move overflow-clip rounded-2xl border border-gray-200 bg-white shadow-xl shadow-black/5">
                <div class="relative flex h-80 min-w-0 overflow-clip rounded-b-2xl">
                  <UiImageWithOverlay
                    :src="getTestimonyImage(itemIndex)"
                    container-class="!absolute inset-0"
                    class="aspect-video size-full object-cover object-top"
                    role="presentation"
                  />

                  <div class="relative isolate flex w-full select-none items-end justify-between p-5">
                    <div>
                      <span class="flex text-lg font-medium text-white">
                        {{ rt(item.author) }}
                      </span>

                      <span class="flex text-white/80">
                        {{ rt(item.location) }}
                      </span>
                    </div>

                    <div class="inline-flex gap-x-1 rounded-full bg-white/10 px-3 py-2 text-base text-white backdrop-blur-sm">
                      <UiIcon
                        v-for="i in 5"
                        :key="i"
                        name="star-solid"
                      />
                    </div>
                  </div>
                </div>

                <div class="p-5 text-lg font-medium before:inline-block before:content-['“'] after:inline-block after:content-['”']">
                  {{ rt(item.comment) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <nav class="flex items-center justify-between gap-x-5">
          <div class="hidden gap-x-2 sm:flex">
            <button
              v-for="(_, index) in scrollSnaps"
              :key="index"
              type="button"
              :class="['size-3 rounded-[50%]', selectedIndex === index ? 'bg-orange-500' : 'bg-gray-200']"
              @click="handleClick(index)"
            />
          </div>

          <div class="ml-auto flex gap-x-2">
            <button
              type="button"
              class="inline-flex size-12 items-center justify-center rounded-lg border transition-all enabled:border-neutral-100 enabled:bg-white enabled:shadow-2xl disabled:border-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
              :disabled="isPrevDisabled"
              @click="handlePrevious"
            >
              <UiIcon
                name="arrow-forward-rounded"
                class="-rotate-180"
              />
            </button>

            <button
              type="button"
              class="inline-flex size-12 items-center justify-center rounded-lg border transition-all enabled:border-neutral-100 enabled:bg-white enabled:shadow-2xl disabled:border-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
              :disabled="isNextDisabled"
              @click="handleNext"
            >
              <UiIcon name="arrow-forward-rounded" />
            </button>
          </div>
        </nav>
      </main>
    </section>
  </div>
</template>
